import {Action, Attributes, Card, openDeleteDialog, openDialog} from 'platform/components';
import {VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {always, isNil} from 'ramda';

import {
  ContractInformationResponseBodyV2,
  useDeleteCustomerContractInformationMutation,
} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {
  ContractInformationForm,
  CustomerVerification,
  getContractSubTitle,
  getContractTitle,
  handleApiError,
  useCustomerContractAddress,
  useCustomerContractAttributes,
  usePermissions,
} from '@dms/shared';

import {buildArray, EMPTY_PLACEHOLDER, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {AdditionalCustomerTypeProps} from '../types/AdditionalCustomerTypeProps';

interface CustomerContractCardProps extends RequiredTestIdProps {
  isSelected: boolean;
  onSelect: () => void;
  customerId: string;
  customerContract: ContractInformationResponseBodyV2;
  recordId?: string;
  resourceId?: string;
  additionalCustomerType?: AdditionalCustomerTypeProps;
  isEditingDisabled?: boolean;
}

export function CustomerContractCard(props: CustomerContractCardProps) {
  const [getContractAddress] = useCustomerContractAddress();
  const [getAttributes] = useCustomerContractAttributes();

  const [canEditContractInformation] = usePermissions({
    permissionKeys: ['editContractInformation'],
  });

  const [deleteContract, {isLoading: isDeleteLoading}] =
    useDeleteCustomerContractInformationMutation();

  // Editing/deleting a contract is blocked either by a generic read-only state
  // (props.isEditingDisabled) or by the lack of the dedicated permission.
  const isContractEditingDisabled = props.isEditingDisabled || !canEditContractInformation;

  const onDelete = () =>
    openDeleteDialog({
      onConfirm: () =>
        deleteContract({
          customerId: props.customerId,
          contractInformationId: props.customerContract.id,
        })
          .unwrap()
          .catch(handleApiError),
    });

  return (
    <Card
      title={getContractTitle(props.customerContract)}
      subtitle={getContractSubTitle(props.customerContract)}
      parameters={[getContractAddress(props.customerContract)]}
      isExpanded={props.isSelected}
      hasSeparator={false}
      variant="inlineWhite"
      isClosedByDefault
      control={{
        type: 'radio',
        value: props.isSelected,
        onChange: props.onSelect,
        isDisabled: props.isEditingDisabled,
      }}
      actions={buildArray<Action>()
        .when(props.additionalCustomerType && props.isSelected, {
          type: 'dropdown-button',
          variant: 'outlined',
          title: match(props.additionalCustomerType?.value)
            .with('OPERATOR', always(i18n.t('entity.accounting.labels.operator')))
            .with('RECIPIENT', always(i18n.t('entity.accounting.labels.recipient')))
            .when(isNil, always(EMPTY_PLACEHOLDER))
            .exhaustive(),
          rightIcon: 'navigation/expand_more',
          menuItems: [
            {
              label: i18n.t('entity.accounting.labels.operator'),
              isDisabled:
                props.additionalCustomerType?.value === 'OPERATOR' || props.isEditingDisabled,
              onClick: () => props.additionalCustomerType?.onChange('OPERATOR'),
            },
            {
              label: i18n.t('entity.accounting.labels.recipient'),
              isDisabled:
                props.additionalCustomerType?.value === 'RECIPIENT' || props.isEditingDisabled,
              onClick: () => props.additionalCustomerType?.onChange('RECIPIENT'),
            },
          ],
        })
        .add({
          type: 'iconButtonWithTooltip',
          severity: 'danger',
          icon: 'action/delete',
          isDisabled: isDeleteLoading || isContractEditingDisabled,
          onClick: onDelete,
          tooltip: {
            label: canEditContractInformation ? undefined : i18n.t('general.labels.noPermission'),
          },
        })
        .add({
          type: 'iconButtonWithTooltip',
          icon: 'image/edit',
          isDisabled: isContractEditingDisabled,
          onClick: () =>
            openDialog(
              <ContractInformationForm
                customerId={props.customerId}
                isEditContactInformation
                contract={props.customerContract}
                data-testid={suffixTestId('editContract', props)}
              />,
              {
                title: i18n.t('entity.customer.actions.editBillingInformation'),
                withAdditionalFooter: true,
                'data-testid': suffixTestId('editContract', props),
              }
            ),
          tooltip: {
            label: canEditContractInformation ? undefined : i18n.t('general.labels.noPermission'),
          },
        })}
      data-testid={suffixTestId('contract', props)}
    >
      <VStack spacing={4} align="flex-start">
        <Attributes
          rows={getAttributes(props.customerContract)}
          data-testid={suffixTestId('info', props)}
        />
        <CustomerVerification
          contractInformationId={props.customerContract.id}
          customerId={props.customerId}
          recordId={props.recordId}
          resourceId={props.resourceId}
          isEditingDisabled={props.isEditingDisabled}
          data-testid={suffixTestId('verification', props)}
        />
      </VStack>
    </Card>
  );
}
